import { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Factory, Ship, Stamp, Zap } from 'lucide-react';
import {
  Column,
  DataTable,
  KPIGrid,
  LoadingState,
  MetricCard,
  PageHeader,
  TableFilters,
} from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  supplyAnalyticsApi,
  type ExpediteRatio,
  type FreightToValue,
  type FreightToValueLine,
  type SupplierLeadTime,
} from '@/api/services';
import { fadeUp, stagger } from '@/lib/animations';
import { cn } from '@/lib/utils';

/**
 * Where the lead time goes, and what the freight costs against the goods.
 *
 * Two questions the existing analytics cannot answer. `LeadTimeAnalysis` reports
 * an average and a spread of the *whole* wait, which tells a planner how variable
 * a supplier is and not what to do about it — three suppliers can average fifty
 * days and be slow in three different places. And freight is recorded per
 * shipment, so nothing said which goods it was spent on.
 *
 * Every figure is nullable and says why when it is null. That is not caution: a
 * lead time of zero, a hot-shot ratio of nought per cent and a freight ratio
 * against unpriced goods all read as measurements while being absences, and each
 * would be acted on.
 */

const STAGE_LABEL = {
  manufacturing: 'Making it',
  transit: 'Moving it',
  clearance: 'Clearing it',
} as const;

const pct = (v: number | null | undefined, dp = 1) =>
  v == null ? '—' : `${(v * 100).toFixed(dp)}%`;

const money = (v: number) =>
  v.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export default function SupplyAnalytics() {
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('lead-time');
  const [search, setSearch] = useState('');

  const [leadTimes, setLeadTimes] = useState<SupplierLeadTime[]>([]);
  const [expedite, setExpedite] = useState<ExpediteRatio | null>(null);
  const [freight, setFreight] = useState<FreightToValue | null>(null);

  const load = useCallback(async () => {
    try {
      const [lt, ex, fr] = await Promise.all([
        supplyAnalyticsApi.leadTimeStages(),
        supplyAnalyticsApi.expedite(),
        supplyAnalyticsApi.freightToValue(),
      ]);
      setLeadTimes(lt);
      setExpedite(ex);
      setFreight(fr);
    } catch {
      toast.error('Could not load supply analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const matches = (haystack: string[]) =>
    !search || haystack.some((h) => h?.toLowerCase().includes(search.toLowerCase()));

  const stageCell = (stage: SupplierLeadTime['manufacturing'], dominant: boolean) =>
    stage.days == null ? (
      // Not zero. One observation is an anecdote, and an average of one printed
      // beside an average of forty reads as the same kind of claim.
      <span className="text-xs text-muted-foreground">
        — <span className="text-[11px]">({stage.observations} seen)</span>
      </span>
    ) : (
      <span className={cn(dominant && 'font-semibold text-status-warning-foreground')}>
        {stage.days}d
        <span className="ml-1 text-[11px] font-normal text-muted-foreground">
          ({stage.observations})
        </span>
      </span>
    );

  const leadTimeColumns: Column<SupplierLeadTime>[] = [
    {
      key: 'supplier',
      header: 'Supplier',
      sortable: true,
      linkTo: (r) => `/suppliers/${r.supplierId}`,
    },
    {
      key: 'manufacturing',
      header: 'Making it',
      className: 'text-right tabular-nums',
      accessorFn: (r) => r.manufacturing.days ?? -1,
      render: (r) => stageCell(r.manufacturing, r.dominantStage === 'manufacturing'),
    },
    {
      key: 'transit',
      header: 'Moving it',
      className: 'text-right tabular-nums',
      accessorFn: (r) => r.transit.days ?? -1,
      render: (r) => stageCell(r.transit, r.dominantStage === 'transit'),
    },
    {
      key: 'clearance',
      header: 'Clearing it',
      className: 'text-right tabular-nums',
      accessorFn: (r) => r.clearance.days ?? -1,
      render: (r) => stageCell(r.clearance, r.dominantStage === 'clearance'),
    },
    {
      key: 'totalDays',
      header: 'Total',
      sortable: true,
      className: 'text-right tabular-nums',
      accessorFn: (r) => r.totalDays ?? -1,
      render: (r) =>
        r.totalDays == null ? (
          // A total assembled from two stages of three is a fragment presented
          // as a lead time.
          <span className="text-xs text-muted-foreground">not all stages seen</span>
        ) : (
          <span className="font-medium">{r.totalDays}d</span>
        ),
    },
    {
      key: 'dominantStage',
      header: 'Where it goes',
      render: (r) =>
        r.dominantStage ? (
          <Badge variant="outline" className="font-normal">
            {STAGE_LABEL[r.dominantStage]}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
  ];

  const freightColumns: Column<FreightToValueLine>[] = [
    {
      key: 'sku',
      header: 'Item',
      sortable: true,
      linkTo: (l) => `/inventory/${l.itemId}`,
      render: (l) => (
        <div>
          <div className="font-medium">{l.sku}</div>
          <div className="text-xs text-muted-foreground">{l.name}</div>
        </div>
      ),
    },
    { key: 'units', header: 'Units moved', sortable: true, className: 'text-right tabular-nums' },
    {
      key: 'freight',
      header: 'Freight (allocated)',
      sortable: true,
      className: 'text-right tabular-nums',
      render: (l) => money(l.freight),
    },
    {
      key: 'value',
      header: 'Goods',
      sortable: true,
      className: 'text-right tabular-nums',
      render: (l) =>
        l.unpriced ? (
          <span className="text-xs text-status-warning-foreground">no cost recorded</span>
        ) : (
          money(l.value)
        ),
    },
    {
      key: 'ratio',
      header: 'Freight vs goods',
      sortable: true,
      className: 'text-right tabular-nums',
      accessorFn: (l) => l.ratio ?? -1,
      render: (l) =>
        l.ratio == null ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          <span
            className={cn(
              'font-medium',
              l.ratio >= 1 && 'text-status-destructive-foreground',
              l.ratio >= 0.5 && l.ratio < 1 && 'text-status-warning-foreground'
            )}
          >
            {pct(l.ratio, 0)}
          </span>
        ),
    },
  ];

  if (loading) return <LoadingState />;

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Supply Analytics"
          description="Where the lead time goes, what is being paid to hurry, and what the freight costs against the goods it carried."
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <KPIGrid columns={4}>
          <MetricCard
            label="Suppliers staged"
            value={leadTimes.filter((l) => l.totalDays != null).length}
            subtitle={`of ${leadTimes.length} with shipments matched`}
            icon={<Factory className="h-4 w-4" />}
          />
          <MetricCard
            label="Expedited runs"
            value={expedite?.countRatio == null ? '—' : pct(expedite.countRatio)}
            subtitle={`${expedite?.expedited ?? 0} of ${expedite?.shipments ?? 0} shipments`}
            icon={<Zap className="h-4 w-4" />}
          />
          <MetricCard
            label="Of the freight spend"
            value={expedite?.spendRatio == null ? 'Unknown' : pct(expedite.spendRatio)}
            subtitle={
              expedite?.pricedShipments
                ? `${expedite.pricedShipments} shipments priced`
                : 'no shipment carries a cost yet'
            }
            sentiment={expedite?.spendRatio == null ? 'warning' : 'neutral'}
            icon={<Ship className="h-4 w-4" />}
          />
          <MetricCard
            label="Items costing more to move"
            value={freight?.lines.filter((l) => (l.ratio ?? 0) >= 1).length ?? 0}
            subtitle="freight above the value of the goods"
            sentiment={
              (freight?.lines.filter((l) => (l.ratio ?? 0) >= 1).length ?? 0) > 0
                ? 'warning'
                : 'neutral'
            }
            icon={<Stamp className="h-4 w-4" />}
          />
        </KPIGrid>
      </motion.div>

      <motion.div variants={fadeUp}>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="lead-time">Lead time</TabsTrigger>
            <TabsTrigger value="expedite">Expediting</TabsTrigger>
            <TabsTrigger value="freight">Freight vs goods</TabsTrigger>
          </TabsList>

          <TabsContent value="lead-time" className="mt-4 space-y-3">
            <DataTable<SupplierLeadTime>
              data={leadTimes.filter((l) => matches([l.supplier]))}
              columns={leadTimeColumns}
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search by supplier…"
                />
              }
              pagination
              exportable
              columnToggle
              emptyTitle="No completed supply legs"
              emptyDescription="A stage is measured from dates already recorded: the order placed, the shipment despatched and arrived, the goods received. Once a purchase order has been shipped and received, its supplier appears here."
            />
            <p className="text-xs text-muted-foreground">
              Three suppliers can average fifty days and be slow in three different places. This
              splits the wait so the next conversation is with whoever owns the slow part: the factory, the forwarder or the customs broker. A stage is blank until it has been
              seen more than once.
            </p>
          </TabsContent>

          <TabsContent value="expedite" className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">What is being paid to hurry</CardTitle>
                <CardDescription>
                  Counted and costed separately on purpose. Four runs in a hundred is a different
                  story from four that cost as much as the other ninety-six, and one ratio cannot
                  say both.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      By count
                    </p>
                    <p className="text-2xl font-semibold tabular-nums">
                      {pct(expedite?.countRatio)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {expedite?.expedited ?? 0} of {expedite?.shipments ?? 0} shipments
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      By spend
                    </p>
                    <p
                      className={cn(
                        'text-2xl font-semibold tabular-nums',
                        (expedite?.spendRatio ?? 0) > (expedite?.countRatio ?? 0) &&
                          'text-status-warning-foreground'
                      )}
                    >
                      {expedite?.spendRatio == null ? 'Unknown' : pct(expedite.spendRatio)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {expedite?.pricedShipments
                        ? `over ${expedite.pricedShipments} priced shipments`
                        : 'no shipment carries a cost — record one to measure this'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Reasons given
                    </p>
                    <ul className="mt-1 space-y-1">
                      {expedite?.reasons.length ? (
                        expedite.reasons.map((r) => (
                          <li key={r.reason} className="text-xs">
                            <span className="font-medium tabular-nums">{r.count}×</span>{' '}
                            <span
                              className={cn(
                                r.reason === 'no reason recorded' &&
                                  'text-status-warning-foreground'
                              )}
                            >
                              {r.reason}
                            </span>
                          </li>
                        ))
                      ) : (
                        <li className="text-xs text-muted-foreground">nothing expedited</li>
                      )}
                    </ul>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Both figures are entered by hand. There is no carrier invoice feed, and nothing
                  infers an expedite from a transport mode. Air freight can be a routine lane; what
                  makes a run a hot shot is the decision to pay more for speed.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="freight" className="mt-4 space-y-3">
            <DataTable<FreightToValueLine>
              data={(freight?.lines ?? []).filter((l) => matches([l.sku, l.name]))}
              columns={freightColumns}
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search by item…"
                />
              }
              pagination
              exportable
              columnToggle
              emptyTitle="No priced shipments"
              emptyDescription="Freight is entered against a shipment by hand. Once one carries a cost, what it was spent on appears here."
            />
            {freight && (
              // The basis, beside the numbers rather than behind a tooltip. These
              // are allocations and reading them as measurements is the mistake
              // this paragraph exists to prevent.
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">Allocated by {freight.basis}.</span>{' '}
                {freight.basisNote}
              </p>
            )}
          </TabsContent>
        </Tabs>
      </motion.div>
    </motion.main>
  );
}
